import { Box } from '@mui/material';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import ImageSettings, { Size } from '../../../../models/BlockComponentSettings/ImageSettings';
import BlockComponentImage from '../../../../models/BlockComponentValues/BlockComponentImage';
import { useGetBase64Image } from '../../../../hooks/UseGetBase64Image';

interface ImagePreviewProps {
  image: BlockComponentImage;
  scale: number;
  crop: Size;
  handleChange: (settings: ImageSettings) => void;
  handleImageSizeChange: (size: Size) => void;
}

const BackgroundImagePreview = ({
  image, scale, crop, handleChange, handleImageSizeChange,
}: ImagePreviewProps) => {
  const positionRef = useRef(image.settings.position);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const base64Image = useGetBase64Image(image.value);

  const handleCrop = useCallback(() => {
    handleChange({
      ...image.settings,
      position: positionRef.current,
      size: {
        width: containerRef.current?.clientWidth,
        height: containerRef.current?.clientHeight,
        borderRadius: image?.settings?.size?.borderRadius,
      },
      frameSize: { width: crop?.width, height: crop?.height },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleChange, image.settings]);

  useEffect(() => {
    handleCrop();
  }, []);

  useEffect(() => {
    if (imageLoaded) {
      handleImageSizeChange({
        width: imageRef.current?.naturalWidth,
        height: imageRef.current?.naturalHeight,
      });
    }
  }, [handleImageSizeChange, imageLoaded]);

  return (
    <Box
      id="ParentBoxHere"
      ref={containerRef}
      sx={{
        width: crop?.width,
        height: crop?.height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          width: crop?.width,
          height: crop?.height,
          overflow: 'auto',
          borderRadius: '4px',
          boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14)',
        }}
      >
        <img
          id="image-preview"
          ref={imageRef}
          src={base64Image}
          alt="being edited"
          role="presentation"
          style={{
            objectFit: 'contain',
            width: '100%',
            height: '100%',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
          onLoad={() => {
            setImageLoaded(true);
          }}
        />
      </Box>
    </Box>
  );
};

export default BackgroundImagePreview;
